/**
 * Подъём плоского чертежа в объём.
 *
 * Здесь нет ни одной строчки three.js: функции превращают планировку в набор
 * ПРИЗМ — плоский контур в координатах плана плюс отметки низа и верха.
 * Так объёмная модель остаётся частью предметной области (её можно проверить,
 * посчитать по ней объём бетона, отдать на бэкенд), а виджет 3D-сцены
 * занимается только тем, что делает из призм меши.
 *
 * Два принципиальных момента:
 *
 * 1. Стена берётся не как отрезок, а как готовый митрованный контур из
 *    `wallPolygons.ts` — тот же, что рисуется на чертеже. Поэтому в углах
 *    стены сходятся без нахлёста и без щелей, ровно как в 2D.
 * 2. Проёмы не «вычитаются» булевой операцией: стена режется на сплошные куски
 *    (простенки, подоконные части, перемычки) тем же `wallPieces`, что и на
 *    чертеже. Это даёт чистую геометрию без CSG и одинаковые дырки в 2D и 3D.
 *
 * Все размеры — в миллиметрах.
 */
import {
	add,
	angleOf,
	clipPolygon,
	dist,
	mul,
	norm,
	pointInPolygon,
	signedArea,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import { detectRooms, type Room } from "./rooms";
import { buildWallOutlines } from "./wallPolygons";
import { openingsOfWall, pointAlongWall, wallPieces } from "./walls";
import {
	WALL_DEFAULTS,
	type Fixture,
	type FloorItem,
	type Opening,
	type RoomCeiling,
	type RoomFloor,
	type WallGraph,
} from "@/shared/plan";
import {
	DEFAULT_WALL_MATERIAL,
	ceilingLook,
	type CeilingLook,
	type WallMaterial,
} from "@/shared/catalog";

/** Призма: плоский контур в координатах плана + отметки низа и верха, мм. */
export interface Prism {
	polygon: Pt[];
	bottom: number;
	top: number;
}

/**
 * Прямоугольник на стороне стены — кусок её грани между проёмами, над дверью или под окном.
 * `from` → `to` — нижнее ребро на линии грани в плане; `t0`/`t1` — те же точки как отступ
 * вдоль оси стены от узла a, мм: по ним рисунок обоев идёт непрерывно через все куски.
 */
export interface FaceQuad {
	from: Pt;
	to: Pt;
	t0: number;
	t1: number;
	bottom: number;
	top: number;
}

/** Одна сторона стены в объёме: 1 — по левой нормали a → b, как `Fixture.side`. */
export interface WallFaceSolid {
	side: 1 | -1;
	/** Наружная нормаль грани в плане, единичная */
	normal: Pt;
	quads: FaceQuad[];
}

/** Одна стена в объёме: её куски, стороны для отделки и то, из чего она сделана. */
export interface WallSolid {
	wallId: string;
	material: WallMaterial;
	height: number;
	parts: Prism[];
	/** Ось стены — по ней сцена понимает, по какой стороне кликнули */
	a: Pt;
	b: Pt;
	faces: WallFaceSolid[];
}

/** Плита пола или потолка комнаты. У пола — запись слоя, как его покрасить знает каталог. */
export interface SlabSolid {
	roomId: string;
	polygon: Pt[];
	bottom: number;
	top: number;
	/** Покрытие комнаты из слоя полов; нет — комната без покрытия */
	floor?: RoomFloor;
	/** Подвесной потолок комнаты; нет — голое перекрытие */
	ceiling?: CeilingLook;
}

/**
 * Проём в мировых координатах: центр, направление стены и габариты.
 * Створку двери и стеклопакет сцена строит уже сама — в локальной системе
 * координат проёма (x вдоль стены, y вверх, z поперёк стены).
 */
export interface OpeningPlacement {
	opening: Opening;
	center: Pt;
	/** Угол стены в плане, радианы */
	angle: number;
	wallThickness: number;
}

/** Точка на стене в мировых координатах — для объёмного вида. */
export interface FixturePlacement {
	fixture: Fixture;
	/** Точка на ОСЕВОЙ линии стены; наружу сдвигает уже сцена */
	center: Pt;
	angle: number;
	wallThickness: number;
}

/** Светильник на потолке в мировых координатах: сам объект и отметка потолка над ним. */
export interface CeilingItemPlacement {
	item: FloorItem;
	/** Высота потолка в этой точке, мм — чистовой потолок комнаты, а без него низ перекрытия */
	ceiling: number;
}

/** Толщина плиты пола и потолка, мм. */
export const SLAB_THICKNESS = 100;

/**
 * Цоколь стены не доходит до пола комнаты на столько, мм: в дверном проёме сверху лежит
 * плита пола, и одна высота дала бы мерцание двух поверхностей.
 */
const PLINTH_GAP = 1;

/** Куски меньше этой площади — мусор после подрезки, их не строим. */
const MIN_PART_AREA = 100;

/**
 * Стены с уже вырезанными проёмами.
 *
 * Крайние куски намеренно не подрезаются по узлам: митрованный контур выходит
 * за узел, и если обрезать его по осевой точке, в углах появятся щели.
 */
export const buildWallSolids = (plan: WallGraph): WallSolid[] => {
	const outlines = buildWallOutlines(plan);
	const solids: WallSolid[] = [];

	for (const wall of Object.values(plan.walls)) {
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		const outline = outlines[wall.id];
		if (!a || !b || !outline) continue;

		const length = dist(a, b);
		if (length < 1) continue;
		const along = norm(sub(b, a));

		const parts: Prism[] = [];
		const pieces = wallPieces(wall, length, openingsOfWall(plan, wall.id));
		for (const piece of pieces) {
			let polygon: Pt[] = [...outline];
			if (piece.x0 > 0) polygon = clipPolygon(polygon, add(a, mul(along, piece.x0)), along);
			if (piece.x1 < length) {
				polygon = clipPolygon(polygon, add(a, mul(along, piece.x1)), mul(along, -1));
			}
			if (polygon.length < 3) continue;
			if (Math.abs(signedArea(polygon)) < MIN_PART_AREA) continue;
			// Стоящий на полу кусок продолжается цоколем вниз, без щели между ними
			parts.push({ polygon, bottom: piece.z0 > 0 ? piece.z0 : -PLINTH_GAP, top: piece.z1 });
		}

		if (parts.length === 0) continue;
		// Цоколь на всю толщину плиты пола: плита уходит под стену только до оси,
		// и без него снаружи под стеной и в дверном проёме виден её торец с текстурой пола
		parts.push({ polygon: outline, bottom: -SLAB_THICKNESS, top: -PLINTH_GAP });
		solids.push({
			wallId: wall.id,
			material: wall.material ?? DEFAULT_WALL_MATERIAL,
			height: wall.height,
			parts,
			a,
			b,
			faces: wallFaces(wall.thickness, a, along, length, outline, pieces),
		});
	}

	return solids;
};

/** Точка контура лежит на линии грани, если отстоит от неё меньше этого, мм. */
const ON_FACE = 1;

/**
 * Стороны стены по кускам. Длина грани берётся из митрованного контура, а не с оси:
 * во внутреннем углу грань короче оси, во внешнем — длиннее, и отделка должна дойти
 * ровно до угла. Проёмы режут грань так же, как стену, — откосы остаются голыми.
 */
const wallFaces = (
	thickness: number,
	a: Pt,
	along: Pt,
	length: number,
	outline: Pt[],
	pieces: { x0: number; x1: number; z0: number; z1: number }[],
): WallFaceSolid[] => {
	const left = { x: -along.y, y: along.x };
	const half = thickness / 2;
	return ([1, -1] as const).flatMap((side) => {
		// Края грани — самые дальние точки контура на её линии
		const onFace = outline
			.filter((p) => Math.abs((p.x - a.x) * left.x + (p.y - a.y) * left.y - side * half) < ON_FACE)
			.map((p) => (p.x - a.x) * along.x + (p.y - a.y) * along.y);
		if (onFace.length < 2) return [];
		const start = Math.min(...onFace);
		const end = Math.max(...onFace);
		const offset = mul(left, side * half);
		const quads: FaceQuad[] = [];
		for (const piece of pieces) {
			const t0 = Math.max(piece.x0 > 0 ? piece.x0 : start, start);
			const t1 = Math.min(piece.x1 < length ? piece.x1 : end, end);
			if (t1 - t0 < 1 || piece.z1 - piece.z0 < 1) continue;
			quads.push({
				from: add(add(a, mul(along, t0)), offset),
				to: add(add(a, mul(along, t1)), offset),
				t0,
				t1,
				bottom: piece.z0,
				top: piece.z1,
			});
		}
		return quads.length > 0 ? [{ side, normal: mul(left, side), quads }] : [];
	});
};

/**
 * Полы комнат. Контур берём по ОСЕВЫМ линиям стен, а не по чистым размерам:
 * так плита уходит под стену и между полом и стеной не остаётся щели.
 */
export const buildFloorSlabs = (
	plan: WallGraph,
	rooms: Room[] = detectRooms(plan),
	roomFloors: Record<string, RoomFloor> = {},
): SlabSolid[] => {
	return rooms.map((room) => {
		const floor = roomFloors[room.id];
		// Подъём пола — единственное из настроек покрытия, что меняет геометрию плиты
		const raise = floor?.raise ?? 0;
		return {
			roomId: room.id,
			polygon: room.outline,
			bottom: -SLAB_THICKNESS + raise,
			top: raise,
			floor,
		};
	});
};

const wallKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

/** Высота потолка комнаты — по самой высокой из её стен. */
export const roomCeilingHeight = (plan: WallGraph, room: Room): number => {
	const byNodes = new Map<string, number>();
	for (const wall of Object.values(plan.walls)) {
		byNodes.set(wallKey(wall.a, wall.b), wall.height);
	}
	let height = 0;
	for (let i = 0; i < room.nodeIds.length; i++) {
		const from = room.nodeIds[i];
		const to = room.nodeIds[(i + 1) % room.nodeIds.length];
		height = Math.max(height, byNodes.get(wallKey(from, to)) ?? 0);
	}
	return height;
};

/**
 * Чистовая высота потолка комнаты: перекрытие минус опуск подвесного потолка.
 * Без подвесного потолка — низ перекрытия.
 */
export const roomFinishedCeilingHeight = (
	plan: WallGraph,
	room: Room,
	ceilings: Record<string, RoomCeiling> = {},
): number => {
	const ceiling = ceilings[room.id];
	const slab = roomCeilingHeight(plan, room);
	return ceiling ? slab - ceilingLook(ceiling).drop : slab;
};

/**
 * Потолки комнат. Верх — по самой высокой из стен комнаты; подвесной потолок
 * опускает низ плиты на свой опуск, и объём между ним и перекрытием заполнен —
 * снаружи его не видно, а изнутри виден чистовой потолок.
 */
export const buildCeilingSlabs = (
	plan: WallGraph,
	rooms: Room[] = detectRooms(plan),
	ceilings: Record<string, RoomCeiling> = {},
): SlabSolid[] =>
	rooms.map((room) => {
		const height = roomCeilingHeight(plan, room);
		const ceiling = ceilings[room.id];
		return {
			roomId: room.id,
			polygon: room.outline,
			bottom: roomFinishedCeilingHeight(plan, room, ceilings),
			top: height + SLAB_THICKNESS,
			ceiling: ceiling ? ceilingLook(ceiling) : undefined,
		};
	});

/**
 * Высота потолка в точке плана: чистовой потолок комнаты, в которую точка попала;
 * вне комнат — по самой высокой стене чертежа, а на пустом чертеже — типовая высота.
 */
export const ceilingHeightAt = (
	plan: WallGraph,
	rooms: Room[],
	point: Pt,
	ceilings: Record<string, RoomCeiling> = {},
): number => {
	const room = rooms.find((candidate) => pointInPolygon(point, candidate.outline));
	if (room) return roomFinishedCeilingHeight(plan, room, ceilings);
	let height = 0;
	for (const wall of Object.values(plan.walls)) height = Math.max(height, wall.height);
	return height > 0 ? height : WALL_DEFAULTS.height;
};

/**
 * Светильники на потолке: каждому — отметка потолка над ним, чтобы 3D повесил его
 * на нужной высоте. Подвесной потолок учитывается, даже если слой потолков скрыт:
 * люстра крепится к натяжному, а не к перекрытию.
 */
export const buildCeilingItemPlacements = (
	plan: WallGraph,
	rooms: Room[],
	items: FloorItem[],
	ceilings: Record<string, RoomCeiling> = {},
): CeilingItemPlacement[] =>
	items.map((item) => ({ item, ceiling: ceilingHeightAt(plan, rooms, item, ceilings) }));

/** Двери и окна, привязанные к мировым координатам. */
export const buildOpeningPlacements = (plan: WallGraph): OpeningPlacement[] => {
	const placements: OpeningPlacement[] = [];

	for (const opening of Object.values(plan.openings)) {
		const wall = plan.walls[opening.wallId];
		if (!wall) continue;
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;
		const length = dist(a, b);
		if (length < 1) continue;

		placements.push({
			opening,
			center: pointAlongWall(a, b, opening.offset),
			angle: angleOf(sub(b, a)),
			wallThickness: wall.thickness,
		});
	}

	return placements;
};

/** Объекты на стенах в мировых координатах: список объектов даёт вызывающий, стены — граф. */
export const buildFixturePlacements = (
	plan: WallGraph,
	fixtures: Fixture[],
): FixturePlacement[] => {
	const placements: FixturePlacement[] = [];

	for (const fixture of fixtures) {
		const wall = plan.walls[fixture.wallId];
		if (!wall) continue;
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;
		if (dist(a, b) < 1) continue;

		placements.push({
			fixture,
			center: pointAlongWall(a, b, fixture.offset),
			angle: angleOf(sub(b, a)),
			wallThickness: wall.thickness,
		});
	}

	return placements;
};

/** Габариты чертежа по осевым линиям — по ним 3D-вид наводит камеру. */
export const planBounds = (plan: WallGraph): { min: Pt; max: Pt } | null => {
	const nodes = Object.values(plan.nodes);
	if (nodes.length === 0) return null;

	const min = { x: Infinity, y: Infinity };
	const max = { x: -Infinity, y: -Infinity };
	for (const node of nodes) {
		min.x = Math.min(min.x, node.x);
		min.y = Math.min(min.y, node.y);
		max.x = Math.max(max.x, node.x);
		max.y = Math.max(max.y, node.y);
	}
	return { min, max };
};
